/**
 * Form helper utilities for the Microservice Generator
 */

import { AdvancedOptionsData, InfraDependenciesData } from '../types/formTypes';
import { getRepositoryPrefix } from '../../../shared/types/microservice-generator';


// Utility to check if database is required for a tech stack
export const isDBRequiredForTechStack = (techStack: string): boolean => {
    return techStack !== 'ReactJS';
};

// Serialize advanced options for form submission
export const serializeAdvancedOptions = (data: AdvancedOptionsData): string => {
    return Object.entries(data)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join(',');
};

// Parse maintainer teams string into array
export const parseMaintainerTeams = (maintainerTeamsString: string): string[] => {
    return maintainerTeamsString
        .split(',')
        .map(team => team.trim())
        .filter(team => team.length > 0);
};

// Get service name placeholder based on tech stack (always for new services)
export const getServiceNamePlaceholderWithType = (techStack?: string): string => {
    const prefix = getRepositoryPrefix(techStack || '');
    const label = 'Enter new Service Name';
    
    return prefix 
        ? `${label} ('${prefix}' is prefixed automatically)`
        : `${label}`;
};

// Form data interface for type safety
export interface FormData {
    ORG: string;
    TECH_STACK: string;
    SERVICE_NAME: string;
    DESCRIPTION: string;
    DOMAIN: string;
    FUNCTIONAL_AREA: string;
    DB_CHOICE: string;
    KAFKA_ENABLED: string;
    REDIS_ENABLED: string;
    MAINTAINER_TEAMS: string[];
    FOR_DXG_ONLY: string;
    TEMPLATE_REPO_BRANCH?: string;
    DEPLOY_REPO_BRANCH?: string;
    GENERATOR_BRANCH_NAME?: string;
    GENERATED_BY?: string;
}

// Create form data object for submission
export const createFormData = (
    techStack: string,
    serviceName: string,
    description: string,
    domain: string,
    functionalArea: string,
    orgName: string,
    maintainerTeams: string,
    infraDependenciesData: InfraDependenciesData,
    advancedOptionsData: AdvancedOptionsData,
    generatedBy: string
): FormData => {
    const maintainerTeamsArray = parseMaintainerTeams(maintainerTeams);
    const forDxgOnlyValue = serializeAdvancedOptions(advancedOptionsData);

    return {
        ORG: orgName,
        TECH_STACK: techStack,
        SERVICE_NAME: serviceName,
        DESCRIPTION: description,
        DOMAIN: domain,
        FUNCTIONAL_AREA: functionalArea,
        DB_CHOICE: infraDependenciesData.database || 'NONE',
        KAFKA_ENABLED: infraDependenciesData.kafka ? 'true' : 'false',
        REDIS_ENABLED: infraDependenciesData.redis ? 'true' : 'false',
        MAINTAINER_TEAMS: maintainerTeamsArray,
        FOR_DXG_ONLY: forDxgOnlyValue,
        TEMPLATE_REPO_BRANCH: advancedOptionsData.templateRepoBranch,
        DEPLOY_REPO_BRANCH: advancedOptionsData.deployRepoBranch,
        GENERATOR_BRANCH_NAME: advancedOptionsData.generatorBranchName,
        GENERATED_BY: generatedBy
    };
};
